<i18n>
{
  "cl": {
    "sectionFilter": "Buscar",
    "create": "Crear risk policy",
    "policyFilter": "Filtrar por etiquetas"
  },
  "pe": {
    "sectionFilter": "Buscar",
    "create": "Crear risk policy",
    "policyFilter": "Filtrar por etiquetas"
  },
  "br": {
    "sectionFilter": "Buscar",
    "create": "Criar risk policy",
    "policyFilter": "Filtrar por etiquetas"
  }
}
</i18n>
<template>
  <div>
    <Spinner id="spinner" :is-loading="isLoading" />
    <div v-if="!isLoading">
      <div id="rp-products">
        <b-button
          icon-left="home"
          active>
          Hipotecario
        </b-button>
        <b-button
          disabled
          icon-left="plus">
          Complementario
        </b-button>
        <b-button
          disabled
          icon-left="hand-holding-dollar">
          Pie hipotecario
        </b-button>
        <b-button
          disabled
          icon-left="building">
          PYME
        </b-button>
        <b-button
          disabled
          icon-left="cart-shopping">
          Consumo
        </b-button>
      </div>
      <div class="card-item">
        <div id="rp-header" class="columns is-mobile my-0">
          <b-field expanded class="column">
            <b-taginput
              id="search-tags"
              v-model="selectedTags"
              :data="filteredTags"
              autocomplete
              field="tags"
              icon="label"
              :placeholder="$t('policyFilter')"
              @input="updateHeaderPolicies"
              @typing="getFilteredTags" />
          </b-field>
          <div class="column is-narrow">
            <b-button
              id="show-new-risk-policy"
              icon-left="fa-solid fa-plus"
              class="is-primary is-light is-small is-rounded"
              @click="showNewPolicy">
              <span class="is-size-7 has-text-weight-normal">
                {{ $t('create') }}
              </span>
            </b-button>
          </div>
        </div>
        <div id="risk-policies-list">
          <Spinner :is-loading="isHeaderPoliciesLoading" />
          <div v-if="!isHeaderPoliciesLoading">
            <div
              v-for="(policy, index) in headerPolicies"
              :key="index"
              :ref="`policy-item-${index}`"
              class="policy-item card-item shadowless"
              :class="headerPolicies[index]?.successApply">
              <div class="policy-actions">
                <b-switch
                  v-model="headerPolicies[index].isActive"
                  class="policy-status"
                  size="is-small"
                  data-cy="policy-status"
                  @input="togglePolicyStatus(policy)" />
                <b-button
                  icon-left="gear"
                  class="policy-details"
                  :aria-expanded="openStates[index]"
                  :aria-controls="`collapse-${index}`"
                  @click="togglePolicyDetails(index, policy._id)" />
              </div>
              <div v-show="!openStates[index]" class="policy-details">
                <p class="title is-size-6 has-text-primary mb-0">{{ policy.policyName }}</p>
                <small class="summary has-text-weight-normal">{{
                  policy.description
                }}</small>
              </div>
              <Spinner :is-loading="isPolicyLoading && index === currPolicyIndex" />
              <b-collapse
                v-model="openStates[index]"
                animation="slide"
                :aria-id="`collapse-${index}`">
                <RiskPolicyDetail
                  v-if="policy && openStates[index]"
                  :policy="policy"
                  :tags="filteredTags"
                  :index="index"
                  @change:policy="handledApply"
                  @cancel="hidePolicyDetail" />
              </b-collapse>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Spinner from '@/ui/components/Spinner.vue';
import RiskPolicyDetail from '@/ui/components/internal/settings/risk-policies/RiskPolicyDetail.vue';
import { RiskPolicyClient } from '@/application/services/risk-policies/risk-policy-client';
import { requestRiskPolicies } from '@/application/api';
import { authenticationService } from '@/application/services/api-clients/login-service';
import defaultPolicy from '@/domain/constants/risk-policies';

export default {
  name: 'RiskPolicy',
  components: { Spinner, RiskPolicyDetail },
  data() {
    return {
      isLoading: true,
      isHeaderPoliciesLoading: false,
      isPolicyLoading: true,
      currPolicyIndex: null,
      isSelectOnly: false,
      openStates: [],
      originalTags: [],
      filteredTags: [],
      selectedTags: [],
      headerPolicies: [],
      riskPolicyClient: new RiskPolicyClient(requestRiskPolicies, authenticationService)
    };
  },
  async mounted() {
    try {
      this.originalTags = await this.fetchUniqueTags();
      this.filteredTags = this.originalTags;
      if (this.originalTags && this.originalTags.length > 0) {
        this.headerPolicies = await this.fetchHeaderPolicy();
        this.hidePolicyDetail();
      }
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    getFilteredTags(text) {
      if (text) {
        this.filteredTags = this.originalTags.filter(tag => tag.toLowerCase().includes(text.toLowerCase()));
      }
    },
    async updateHeaderPolicies() {
      this.headerPolicies = await this.fetchHeaderPolicy(this.selectedTags);
      this.hidePolicyDetail();
    },
    async togglePolicyStatus(policy) {
      await this.riskPolicyClient.isActive(policy._id, policy.isActive);
    },
    async togglePolicyDetails(index, policyId) {
      if (this.openStates[index] === true) {
        this.openStates[index] = false;
        this.currPolicyIndex = null;
      } else if (this.openStates[index] === false) {
        this.currPolicyIndex = index;
        this.openStates = this.openStates.map(_state => false);
        if (policyId) {
          this.headerPolicies[index] = await this.fetchPolicy(policyId);
        } else {
          this.headerPolicies[index] = this.copyDefault();
        }
        this.openStates[index] = true;
      }
      this.$forceUpdate();
    },
    copyDefault() {
      return JSON.parse(JSON.stringify(defaultPolicy));
    },
    async fetchUniqueTags() {
      this.isLoading = true;
      try {
        return this.riskPolicyClient.getUniqueTags();
      } catch (error) {
        this.$log.error(`Error fetching unique tags. Cause: ${error.message}`);
      } finally {
        this.isLoading = false;
      }
      return [];
    },
    async fetchHeaderPolicy(tags) {
      let selectedTags = tags;
      if (!tags) {
        selectedTags = [];
      }
      this.isHeaderPoliciesLoading = true;
      try {
        const dataRiskPolicies = await this.riskPolicyClient.getPolicies(selectedTags, true);
        return dataRiskPolicies.data;
      } catch (error) {
        this.$log.error(`Error fetching risk policies. Cause: ${error.message}`);
      } finally {
        this.isHeaderPoliciesLoading = false;
      }
      return [];
    },
    async fetchPolicy(policyId) {
      this.isPolicyLoading = true;
      try {
        const detail = await this.riskPolicyClient.getPolicyDetails(policyId);
        return detail.data;
      } catch (error) {
        this.$log.error(`Error fetching policy details. Cause: ${error.message}`);
      } finally {
        this.isPolicyLoading = false;
      }
      return null;
    },
    showNewPolicy() {
      const newPolicy = this.copyDefault();
      this.headerPolicies.unshift(newPolicy);
      this.openStates = this.headerPolicies.map((_, index) => index === 0);
      this.$forceUpdate();
    },
    async handledApply(data) {
      const { policy } = data;
      const { index } = data;
      if (policy._id) {
        await this.updatePolicy(policy, index);
      } else {
        await this.createPolicy(policy, index);
      }
    },
    async updatePolicy(policy, index) {
      try {
        const policyData = await this.riskPolicyClient.updatePolicy(policy);
        this.headerPolicies[index] = policyData.data;
        this.openStates[index] = !this.openStates[index];
        this.toggleSuccessClass(index);
      } catch (error) {
        this.$log.error(`Error updating the policy. Cause: ${error.message}`);
      }
      this.$forceUpdate();
    },
    async createPolicy(data, index) {
      try {
        const policyData = await this.riskPolicyClient.createPolicy(data);
        this.headerPolicies[index] = policyData.data;
        this.openStates[index] = !this.openStates[index];
        this.toggleSuccessClass(index);
      } catch (error) {
        this.$log.error(`Error creating the policy. Cause: ${error.message}`);
      }
      this.$forceUpdate();
    },
    hidePolicyDetail(index) {
      this.openStates = this.headerPolicies.map(() => false);
      const ele = this.$refs[`policy-item-${index}`];
      if (ele) {
        ele[0].scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.$forceUpdate();
      }
    },
    toggleSuccessClass(index, duration = 2000) {
      this.headerPolicies[index].successApply = { 'is-success': true };
      window.scrollTo(0, 0);
      setTimeout(() => {
        this.headerPolicies[index].successApply = {};
        this.$forceUpdate();
      }, duration);
    }
  }
};
</script>
<style>
#rp-products {
  margin-bottom: 1rem;
}

#rp-products button {
  margin-right: 5px;
  background-color: transparent;
  border-color: transparent;
}

#rp-products button:hover,
#rp-products button:focus {
  background-color: rgba(0, 0, 0, 0.05);
}

#rp-products button.is-active {
  background-color: var(--primary);
  color: #FFF;
}

/* Policy items */
#risk-policies-list {
  min-height: 200px;
}

.card-item:not(:last-child) {
  margin-bottom: 1rem;
}

.policy-item.is-success {
  border-color: var(--primary);
}

.policy-item .policy-actions {
  display: flex;
  align-items: center;
  position: fixed;
  right: 30px;
}

.policy-item .policy-actions button {
  background-color: transparent;
  padding: 0;
  border: none;
  color: #4D4D4D;
  font-size: 1rem;
  cursor: pointer;
  margin-left: 1rem;
}

.policy-item .policy-actions button:hover {
  color: var(--primary);
}

.policy-item .policy-actions button:focus:not(:active),
.policy-item .policy-actions button.is-focused:not(:active) {
  box-shadow: none;
}

.fields-policies .field .label {
  text-align: left;
  font-weight: 400;
  font-size: 0.875rem;
}

@media screen and (min-width: 768px) {
  .policy-item .buttons {
    justify-content: flex-end;
  }

  .fields-policies .is-horizontal .field-label {
    flex-grow: 4;
  }
}

@media screen and (max-width: 768px) {
  .policy-item .policy-details {
    max-width: 72%;
  }
}

</style>
